import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useSelector } from 'react-redux';
import { cn } from '../utils/utils';
import { LockKeyhole, Loader2 } from 'lucide-react/dist/cjs/lucide-react';
import { Box } from '@mui/material';
import { pricing } from '../redux/slices/pricingSlice';
import amplitude from "../utils/ampli";
import { auth } from "../redux/slices/authSlice";

const BulkLockPMI =({
  selectedItems,
  filters,
  allSelected,
  searchParam,
  activeTab,
  criteriaMap,
  criteriaData,
  refreshMarketItems,
  setMappedItemSearchCriteria,
  setCurrentPage,
  setMarketFrom,
  checked,
  setChecked,
  userdiamondId,
}) => {
  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showAllFilters, setShowAllFilters] = React.useState(false);
  const accountDetails = useSelector(auth).userDetails;
  const customFiltersRaw = useSelector(pricing)?.config?.custom_filters ?? [];
  const customFiltersRawByKey = React.useMemo(
    () => Object.fromEntries(customFiltersRaw.map((f) => [f.key, f])),
    [customFiltersRaw]
  );
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleBulkLock = async () => {
    if (!criteriaData || !refreshMarketItems) {
      handleClose();
      return;
    }

    if (!accountDetails?.account_id) {
      handleClose();
      return;
    }

    setIsLoading(true);
    const marketFilters = {};
    Object.entries(criteriaData || {}).forEach(([key, item]) => {
      if (!item || item.value == null) return;
      if (Array.isArray(item.value)) {
        const selectedKeys = item.value.reduce((acc, v) => {
          if (v?.is_selected) acc.push(v.key);
          return acc;
        }, []);
        marketFilters[key] = selectedKeys;
      } else {
        marketFilters[key] = {
          from: item.value.from || item.value.min,
          to: item.value.to || item.value.max,
        };
      }
    });
    marketFilters["is_competition"] = checked;

    const buttonClickedEvent = {
      event_type: `v4_itemDetails_filter_and_lock`,
      event_properties: {
        value: criteriaData,
        id: userdiamondId,
      },
    };
    amplitude.track(buttonClickedEvent);

    if (setMappedItemSearchCriteria) {
      setMappedItemSearchCriteria(criteriaData);
    }

    const targetItemIds = [];
    const seen = new Set();
    selectedItems.forEach((item) => {
      const itemId = item?.["LD ID"] ?? item?.["Item ID"];
      const isPCP = item?.["Is PCP Present"] == true;
      if (!itemId || !isPCP) return;
      const key = `${itemId}`;
      if (seen.has(key)) return;
      seen.add(key);
      targetItemIds.push(itemId);
    });

    if (targetItemIds.length === 0) {
      setIsLoading(false);
      handleClose();
      return;
    }

    if (setCurrentPage) {
      setCurrentPage(1);
    }
    if (setMarketFrom) {
      setMarketFrom(0);
    }

    refreshMarketItems({
      isReset: false,
      isFilter: true,
      isDefault: false,
      lock: true,
      isCompetition: checked,
      isBroad: false,
      filters: criteriaData,
      itemIds: targetItemIds,
    });

    if (setChecked) {
      setChecked(false);
    }

    setIsLoading(false);
    handleClose();
  };
   

   // Deduplicate [LD ID] and [Cert Num] values
  const { uniqueLDIDs } = React.useMemo(() => {
    // if (allSelected) {
    //   return { uniqueLDIDs: ["All"] };
    // }
    const ldIds = new Set();
    selectedItems.forEach(item => {
      const resolvedId =
        item?.["Stock No"] ??
        item?.["Item ID"];
        const isPCP = item?.["Is PCP Present"] == true;
      if (resolvedId != null && isPCP) {
        ldIds.add(resolvedId);
      }
    });
    return { 
      uniqueLDIDs: Array.from(ldIds)
    };
  }, [selectedItems, allSelected]);

  const appliedFiltersMap = React.useMemo(() => {
    if (criteriaMap && Object.keys(criteriaMap).length > 0) return criteriaMap;
    if (!Array.isArray(filters) || filters.length === 0) return {};

    const map = {};
    const getLabel = (id) => customFiltersRawByKey[id]?.label || id;
    const customEnumOptions = Object.fromEntries(
      Object.entries(customFiltersRawByKey).map(([k, v]) => [k, v?.options || []])
    );

    filters.forEach((f) => {
      if (!f || f.value == null) return;
      const id = f.id;

      if (id === "Measurements" && typeof f.value === "object" && !Array.isArray(f.value)) {
        Object.entries(f.value).forEach(([sub, range]) => {
          if (
            range &&
            typeof range === "object" &&
            range.min !== undefined &&
            range.max !== undefined
          ) {
            map[getLabel(sub)] = `${range.min} - ${range.max}`;
          }
        });
        return;
      }

      if (
        typeof f.value === "object" &&
        !Array.isArray(f.value) &&
        f.value.min !== undefined &&
        f.value.max !== undefined
      ) {
        map[getLabel(id)] = `${f.value.min} - ${f.value.max}`;
        return;
      }

      if (Array.isArray(f.value)) {
        if (customEnumOptions[id]) {
          const all = customEnumOptions[id];
          const excluded = new Set(f.value);
          const included = all.filter((opt) => !excluded.has(opt));
          if (included.length > 0 && included.length !== all.length) {
            map[getLabel(id)] = included.join(", ");
          }
        } else if (f.value.length > 0) {
          map[getLabel(id)] = f.value.join(", ");
        }
      }
    });

    return map;
  }, [criteriaMap, filters, customFiltersRawByKey]);

  const orderedAppliedFiltersEntries = React.useMemo(() => {
  const rawEntries = Object.entries(appliedFiltersMap || {});
  const lastKeys = [
    "Length",
    "Width",
    "Height",
    "Ratio",
    "Table%",
    "Depth%",
    "Crown Angle",
    "Pav Angle",
  ];

  const lastKeySet = new Set(lastKeys);
  const entryMap = new Map(rawEntries);

  const firstEntries = rawEntries.filter(([key]) => !lastKeySet.has(key));
  const lastEntries = lastKeys
    .filter((key) => entryMap.has(key))
    .map((key) => [key, entryMap.get(key)]);

  return [...firstEntries, ...lastEntries];
}, [appliedFiltersMap]);

  const toggleAllFilters = (event) => {
    event.stopPropagation();
    setShowAllFilters((prev) => !prev);
  };

  return (
    <div className=' w-max flex justify-center'>
        <div
            className={cn(
            "self-end flex gap-[0.200rem] items-center justify-center h-8  text-[#1E90FF] font-medium text-[0.75rem] w-max",
            selectedItems.length > 0
                ? "bg-transparent  text-[#1E90FF] cursor-pointer"
                : "bg-transparent opacity-40 border-opacity-40 cursor-not-allowed text-[#1E90FF]"
            )}
            onClick={handleClickOpen}
            disabled={selectedItems.length === 0 }
        >
            <LockKeyhole className="h-4 w-4" /> Bulk Lock
        </div>
        

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: 'form',
          className: "p-2",
          onSubmit: (event) => {
            event.preventDefault();
          },
        }}
      >
        <DialogTitle className='self-center font-inter'>Lock your stone(s) criteria</DialogTitle>
        <DialogContent>
          <DialogContentText className='mb-6  font-inter text-xs'>
            Below are the stock numbers for the selected <strong>{allSelected ?  uniqueLDIDs.length : uniqueLDIDs.length}</strong> stones. 
            Please review and confirm to proceed.
          </DialogContentText>      
          <DialogContentText className='mb-6'>
          </DialogContentText>      
          <DialogContentText className='flex gap-2  font-inter text-xs'>
          Stock No: 
          <Box
          className='flex flex-wrap gap-2 w-[75%] h-auto max-h-40 overflow-y-auto  font-inter text-xs'
          sx={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '4px',
          }}
        >
          {uniqueLDIDs.map((ldId, index) => (
                <p
                  key={index}
                  className='font-medium text-xs'
                  sx={{
                    margin: '2px',
                    fontSize: '0.75rem',
                  }}
                >{ldId}{index < uniqueLDIDs.length - 1 ? ',' : ''}</p>
              ))}
        </Box>
        </DialogContentText>
        {Object.keys(appliedFiltersMap).length > 0 ? (
          <div className="mt-3">
            <div className="text-xs font-medium text-[#838383]">Applied Filters</div>
            <div className="flex flex-wrap gap-1 mt-1 items-center">
              {orderedAppliedFiltersEntries
                  .slice(0, showAllFilters ? undefined : 4)
                  .map(([key, value]) => (
                  <div
                    key={key}
                    className="rounded-md border border-[#E5E7EB] bg-[#F8FAFC] px-2 py-[2px] text-[0.65rem]"
                  >
                    <span className="font-semibold text-[#4B5563]">{key}:</span>
                    <span className="ml-1 text-[#6B7280]">{value.toUpperCase()}</span>
                  </div>
                ))}
              {Object.keys(appliedFiltersMap).length > 4 ? (
                <div
                  onClick={toggleAllFilters}
                  className="rounded-md border border-[#1E90FF] text-[#1E90FF] px-2 py-[2px] text-[0.65rem] cursor-pointer"
                >
                  {showAllFilters
                    ? "Show less"
                    : `+${Object.keys(appliedFiltersMap).length - 4} more`}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        </DialogContent>
        <DialogActions>
          <button
            className={cn(
                    " rounded-md flex gap-[0.200rem] items-center justify-center px-3 h-8 border-solid border-[1px] border-[#1E90FF] text-[#1E90FF] font-medium text-[0.75rem] w-max bg-transparent"
                    )}
            onClick={handleClose}
            disabled={isLoading}
            >
            Cancel
          </button>
          <button
            className={cn(
                    " rounded-md flex gap-[0.200rem] items-center justify-center px-4 hover:bg-blue-300 h-8 border-none font-medium text-[0.75rem] w-max",
                    isLoading
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-[#A4D9FF] hover:bg-blue-300"
                )}
            onClick={handleBulkLock} 
            type="submit" 
            disabled={isLoading}>
               {isLoading ? (
                <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Locking...
              </>
               ) : (
                "Confirm"
               )}
            
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
export default BulkLockPMI
